import { depuisUtm, versUtm } from './utm.js';

/**
 * LE GISEMENT, FRAGMENT PARTAGÉ — déplacé depuis `src/domaine/cogo.ts` le
 * 11/10/2026 pour l'item D.946 (saisie COGO dans l'atelier de tracé).
 *
 * ⚠️⚠️ DÉPLACÉ, JAMAIS RECOPIÉ. L'écran de l'atelier (frontend) a besoin de
 * la conversion gisement→point, et le frontend n'importe QUE `src/partage/`
 * (l'étage `interface` du Dockerfile ne copie pas `src/domaine` — garde
 * `tests/frontiere-etage-interface.test.ts`). Recopier `versRadians` dans
 * l'écran aurait fait deux implémentations qui divergent au premier
 * correctif ; le fragment pur vit donc ICI, et `src/domaine/cogo.ts` le
 * RÉIMPORTE — un seul code, deux consommateurs.
 *
 * ⚠️ LE GISEMENT N'EST PAS L'ANGLE MATHÉMATIQUE. Il se compte depuis le NORD,
 * en SENS HORAIRE — `atan2(Δest, Δnord)`, dans cet ordre. L'inversion est le
 * piège n° 1 du COGO et elle est indétectable à l'œil sur un angle proche de
 * 45° : voir l'en-tête de `src/domaine/cogo.ts`.
 */

export type UniteAngle = 'grade' | 'degre';

/** Un tour complet, dans chaque unité. Le gon vaut 400 au tour, le degré 360. */
export const TOUR: Record<UniteAngle, number> = { grade: 400, degre: 360 };

export function versRadians(valeur: number, unite: UniteAngle): number {
  return (valeur / TOUR[unite]) * 2 * Math.PI;
}

export function depuisRadians(radians: number, unite: UniteAngle): number {
  const v = (radians / (2 * Math.PI)) * TOUR[unite];
  const t = TOUR[unite];
  return ((v % t) + t) % t;
}

/**
 * Gisement d'un vecteur, en radians depuis le nord, sens horaire.
 *
 * ⚠️ `atan2(Δest, Δnord)` — DANS CET ORDRE. `atan2(Δnord, Δest)` rendrait
 * l'angle mathématique : c'est le piège n° 1, et il est indétectable à l'œil.
 */
export function gisementRadians(dEst: number, dNord: number): number {
  const a = Math.atan2(dEst, dNord);
  return a < 0 ? a + 2 * Math.PI : a;
}

/**
 * AVANCER d'une distance au gisement donné, depuis un point géodésique.
 *
 * ⚠️ LE CALCUL SE FAIT EN PLAN UTM, PAS EN DEGRÉS. Ajouter des mètres à des
 * degrés exigerait un facteur par axe et par latitude — la forme exacte qui
 * se trompe de 1,4 % à Conakry. On projette (la projection est celle que
 * `tests/utm.test.ts` confronte à PostGIS au millimètre), on avance dans le
 * plan, on déprojette DANS LA MÊME ZONE — un point proche de la coupure
 * 12°W ne doit pas changer de fuseau entre l'aller et le retour.
 */
export function avancerAuGisement(
  lon: number, lat: number, gisement: number, unite: UniteAngle, distanceM: number,
): { lon: number; lat: number } {
  const p = versUtm(lon, lat);
  const g = versRadians(gisement, unite);
  const arrivee = depuisUtm(p.x + distanceM * Math.sin(g), p.y + distanceM * Math.cos(g), p.zone);
  return { lon: arrivee.longitude, lat: arrivee.latitude };
}
